import { Check, CircleCheck, CircleDashed, Copy, Info, SkipForward, Timer, TriangleAlert } from '@orgus/ui/icons';
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { ONBOARDING_TARGET_MS, journeyTime } from '@orgus/protocol';
import type { OnboardingState, OnboardingStepStatus } from '@orgus/protocol';
import { IconButton, cn, formatDuration } from '@orgus/ui';

const STATUS_LABEL: Record<OnboardingStepStatus, string> = { done: 'Done', skipped: 'Skipped', current: 'Next', todo: 'To do' };

/** A step's status as icon plus label (the label is for screen readers when `bare`). */
export function StepStatusIcon({ status, bare, className }: { status: OnboardingStepStatus; bare?: boolean; className?: string }) {
  const label = STATUS_LABEL[status];
  const icon =
    status === 'done' ? (
      <CircleCheck className="size-4 text-passed" aria-hidden />
    ) : status === 'skipped' ? (
      <SkipForward className="size-4 text-fg-subtle" aria-hidden />
    ) : status === 'current' ? (
      <span className="relative flex size-4 items-center justify-center" aria-hidden>
        <span className="absolute inset-0 rounded-full border-[1.5px] border-accent" />
        <span className="size-1.5 rounded-full bg-accent" />
      </span>
    ) : (
      <CircleDashed className="size-4 text-fg-faint" aria-hidden />
    );
  return (
    <span className={cn('inline-flex shrink-0 items-center gap-2', className)}>
      {icon}
      {bare ? <span className="sr-only">{label}</span> : <span className="text-caption text-fg-subtle">{label}</span>}
    </span>
  );
}

/** A line of copyable text: a command or an address. */
export function CopyLine({ text, label, mono = true }: { text: string; label: string; mono?: boolean }) {
  const [copied, setCopied] = useState<boolean | 'failed'>(false);
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1800);
    return () => clearTimeout(t);
  }, [copied]);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      setCopied('failed');
    }
  };
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <div className="flex min-w-0 items-center gap-2 rounded-control border border-border bg-bg py-1 pl-3 pr-1">
        <code className={cn('min-w-0 flex-1 select-all break-all py-1 text-caption leading-relaxed text-fg', mono ? 'font-mono' : 'font-sans')} aria-label={label}>
          {text}
        </code>
        <IconButton label={copied === true ? 'Copied' : `Copy ${label.toLowerCase()}`} icon={copied === true ? <Check className="text-passed" /> : <Copy />} size="sm" onClick={copy} />
      </div>
      {copied === 'failed' && (
        <p role="alert" className="text-caption text-status-danger">
          Not copied: the browser blocked the clipboard. Select the text and copy it by hand.
        </p>
      )}
    </div>
  );
}

/** Inline code in running text. Short commands stay on one line; `wrap` lets long URLs break. */
export function Code({ children, wrap }: { children: ReactNode; wrap?: boolean }) {
  return <code className={cn('rounded bg-bg px-1 py-1 font-mono text-caption text-fg ring-1 ring-border-subtle', wrap ? 'break-all' : 'whitespace-nowrap')}>{children}</code>;
}

/** A tinted note: info for how things work, warning for something to fix, ok for a check that passed. */
export function Callout({ tone = 'info', title, children, action, className }: { tone?: 'info' | 'warning' | 'ok'; title: ReactNode; children?: ReactNode; action?: ReactNode; className?: string }) {
  const Icon = tone === 'warning' ? TriangleAlert : tone === 'ok' ? CircleCheck : Info;
  return (
    <div
      role={tone === 'warning' ? 'alert' : undefined}
      className={cn(
        'flex gap-3 rounded-control border px-3 py-3 text-ui',
        tone === 'warning' ? 'border-waiting/30 bg-waiting-soft' : tone === 'ok' ? 'border-passed/30 bg-passed-soft' : 'border-border-subtle bg-surface',
        className,
      )}
    >
      <Icon className={cn('mt-1 size-4 shrink-0', tone === 'warning' ? 'text-waiting' : tone === 'ok' ? 'text-passed' : 'text-fg-subtle')} aria-hidden />
      <div className="min-w-0 flex-1">
        <div className="font-medium text-fg">{title}</div>
        {children && <div className="mt-1 text-fg-muted">{children}</div>}
      </div>
      {action && <div className="shrink-0 self-center">{action}</div>}
    </div>
  );
}

/** A numbered instruction. */
export function Instruction({ n, title, children }: { n: number; title: ReactNode; children?: ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-raised text-caption font-medium text-fg-muted ring-1 ring-border num" aria-hidden>
        {n}
      </span>
      <div className="min-w-0 flex-1 pb-1">
        <p className="text-ui font-medium text-fg">{title}</p>
        {children && <div className="mt-1 flex flex-col gap-2 text-ui text-fg-muted">{children}</div>}
      </div>
    </li>
  );
}

/** R-ON-1: how long it took from opening Getting started to a running session. Nothing until one ran. */
export function JourneyLine({ state }: { state: Pick<OnboardingState, 'startedAt' | 'journey'> }) {
  const t = journeyTime(state);
  if (!t) return null;
  return (
    <p className="flex items-center gap-2 text-caption text-fg-subtle">
      <Timer className={cn('size-3 shrink-0', t.withinTarget ? 'text-passed' : 'text-fg-faint')} aria-hidden />
      {/* The hub stamps a session already running as Getting started opens at startedAt itself. */}
      {t.ms === 0 ? (
        <span>A session was already live</span>
      ) : (
        <span>
          First session live after <span className="num text-fg-muted">{formatDuration(t.ms)}</span>
          {!t.withinTarget && <>, over the target of {formatDuration(ONBOARDING_TARGET_MS)}</>}
        </span>
      )}
    </p>
  );
}

/** The progress of the five steps as a thin bar with its count. */
export function ProgressBar({ done, total, className }: { done: number; total: number; className?: string }) {
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <div className={cn('flex items-center gap-3', className)}>
      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-raised ring-1 ring-border-subtle" role="progressbar" aria-valuemin={0} aria-valuemax={total} aria-valuenow={done} aria-label="Getting started progress">
        <div className="h-full rounded-full bg-accent transition-[width] duration-300 ease-out" style={{ width: `${pct}%` }} />
      </div>
      <span className="shrink-0 text-caption text-fg-subtle num">
        {done} of {total} done
      </span>
    </div>
  );
}
